import { useMemo } from "react";
import { cn } from "@/lib/utils";
import type { WiringPin } from "@/lib/projectWiringTypes";

export type WiringIsometricSvgProps = {
  pins: WiringPin[];
  mcuLabel?: string;
  selectedPinId?: string | null;
  onSelectPin?: (pinId: string) => void;
  className?: string;
};

type SignalKind = "sda" | "scl" | "digital" | "gnd" | "power" | "other";

type DestKey = "i2c" | "relay" | "buck" | "other";

type WireRoute = {
  pin: WiringPin;
  kind: SignalKind;
  color: string;
  dest: DestKey;
  /** Pad center on ESP32 right edge (svg coords). */
  from: { x: number; y: number };
  /** Terminal on peripheral. */
  to: { x: number; y: number };
  midX: number;
};

const SIGNAL_COLORS: Record<SignalKind, string> = {
  sda: "#38bdf8",
  scl: "#eab308",
  digital: "#fb923c",
  gnd: "#a1a1aa",
  power: "#f87171",
  other: "#94a3b8",
};

function classifySignal(signal: string, pin: string): SignalKind {
  const s = `${signal ?? ""} ${pin ?? ""}`.toUpperCase();
  if (/\bSDA\b/.test(s)) return "sda";
  if (/\bSCL\b/.test(s)) return "scl";
  if (/\bGND\b|GROUND/.test(s)) return "gnd";
  if (/\bVBUS\b|VIN|5V|3V3|3\.3V|VCC|POWER/.test(s)) return "power";
  if (/DIGITAL|OUT|GPIO|RELAY|IN1/.test(s)) return "digital";
  return "other";
}

function classifyDest(to: string): DestKey {
  const t = (to ?? "").toUpperCase();
  if (/I2C|OLED|SENSOR|SDA|SCL/.test(t)) return "i2c";
  if (/RELAY/.test(t)) return "relay";
  if (/BUCK|5\.0|VIN|POWER|ALIMENT/.test(t)) return "buck";
  if (/GND/.test(t) && /BUCK|RELAY|COMÚN|COMUN/.test(t)) return "buck";
  return "other";
}

/** Stable vertical slots on ESP32 right edge for up to 8 pads. */
function esp32PadY(index: number, total: number): number {
  const top = 52;
  const bottom = 148;
  if (total <= 1) return (top + bottom) / 2;
  const span = bottom - top;
  return top + (span * index) / (total - 1);
}

const DEST_TERMINALS: Record<DestKey, { baseX: number; baseY: number; label: string }> = {
  i2c: { baseX: 248, baseY: 48, label: "I2C / OLED" },
  relay: { baseX: 248, baseY: 108, label: "Relay IN1" },
  buck: { baseX: 248, baseY: 168, label: "Buck 5V" },
  other: { baseX: 248, baseY: 138, label: "Periférico" },
};

function destTerminalPoint(dest: DestKey, slot: number, slotsInDest: number): { x: number; y: number } {
  const base = DEST_TERMINALS[dest];
  const offset = slotsInDest <= 1 ? 0 : (slot - (slotsInDest - 1) / 2) * 10;
  return { x: base.baseX - 6, y: base.baseY + offset };
}

/**
 * Fritzing / Learn-Arduino style top-down wiring diagram.
 * Maps ProjectWiring.pins → color jumper wires from ESP32 pads to enclosure terminals.
 */
export function WiringIsometricSvg({
  pins,
  mcuLabel = "ESP32",
  selectedPinId = null,
  onSelectPin,
  className,
}: WiringIsometricSvgProps) {
  const routes = useMemo((): WireRoute[] => {
    if (!pins.length) return [];

    const destSlots = new Map<DestKey, number>();
    const destCounts = new Map<DestKey, number>();
    for (const pin of pins) {
      const d = classifyDest(pin.to);
      destCounts.set(d, (destCounts.get(d) ?? 0) + 1);
    }

    return pins.map((pin, index) => {
      const kind = classifySignal(pin.signal, pin.pin);
      const dest = classifyDest(pin.to);
      const slot = destSlots.get(dest) ?? 0;
      destSlots.set(dest, slot + 1);
      const slotsInDest = destCounts.get(dest) ?? 1;
      const from = { x: 118, y: esp32PadY(index, pins.length) };
      const to = destTerminalPoint(dest, slot, slotsInDest);
      return {
        pin,
        kind,
        color: SIGNAL_COLORS[kind],
        dest,
        from,
        to,
        midX: 168 + (index % 3) * 8,
      };
    });
  }, [pins]);

  const activeDests = useMemo(() => {
    const set = new Set<DestKey>();
    for (const r of routes) set.add(r.dest);
    return set;
  }, [routes]);

  if (pins.length === 0) return null;

  const shortMcu =
    mcuLabel.length > 16 ? mcuLabel.replace(/DevKit/i, "").trim().slice(0, 14) : mcuLabel;

  return (
    <svg
      viewBox="0 0 320 210"
      className={cn("w-full max-w-md mx-auto select-none", className)}
      role="img"
      aria-label={`Diagrama de cableado: ${mcuLabel}`}
    >
      <defs>
        <linearGradient id="wiring-pcb" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#14532d" />
          <stop offset="100%" stopColor="#052e16" />
        </linearGradient>
        <filter id="wiring-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Enclosure floor (subtle iso rhombus) */}
      <path
        d="M40 28 L160 18 L280 38 L160 52 Z"
        fill="rgba(56,189,248,0.04)"
        stroke="rgba(56,189,248,0.18)"
        strokeWidth="1"
      />
      <text
        x="160"
        y="14"
        textAnchor="middle"
        fill="rgba(148,163,184,0.75)"
        fontSize="7"
        fontFamily="ui-monospace, monospace"
        letterSpacing="0.12em"
      >
        GABINETE PETG IP67
      </text>

      {/* ESP32 board */}
      <g>
        <rect
          x="28"
          y="40"
          width="90"
          height="120"
          rx="5"
          fill="url(#wiring-pcb)"
          stroke="#22c55e"
          strokeWidth="1.4"
        />
        {/* Chip die */}
        <rect
          x="48"
          y="72"
          width="40"
          height="36"
          rx="2"
          fill="#052e16"
          stroke="#4ade80"
          strokeWidth="0.8"
          opacity="0.9"
        />
        <text
          x="68"
          y="88"
          textAnchor="middle"
          fill="#86efac"
          fontSize="7"
          fontWeight="700"
          fontFamily="ui-monospace, monospace"
        >
          ESP32
        </text>
        <text
          x="68"
          y="98"
          textAnchor="middle"
          fill="#4ade80"
          fontSize="5.5"
          fontFamily="ui-monospace, monospace"
        >
          S3
        </text>
        {/* USB-C */}
        <rect
          x="54"
          y="148"
          width="28"
          height="8"
          rx="2"
          fill="#1e293b"
          stroke="#94a3b8"
          strokeWidth="0.9"
        />
        <text
          x="68"
          y="168"
          textAnchor="middle"
          fill="#94a3b8"
          fontSize="6"
          fontFamily="ui-monospace, monospace"
        >
          USB-C
        </text>
        <text
          x="73"
          y="56"
          textAnchor="middle"
          fill="#bbf7d0"
          fontSize="6.5"
          fontWeight="600"
          fontFamily="ui-monospace, monospace"
        >
          {shortMcu}
        </text>
        {/* Left silk pads (decorative) */}
        {Array.from({ length: 6 }).map((_, i) => (
          <rect
            key={`L${i}`}
            x="32"
            y={58 + i * 14}
            width="6"
            height="5"
            rx="0.8"
            fill="#334155"
            stroke="#64748b"
            strokeWidth="0.5"
          />
        ))}
      </g>

      {/* Peripheral modules */}
      {activeDests.has("i2c") && (
        <PeripheralBlock
          x={220}
          y={28}
          w={88}
          h={42}
          title="I2C / OLED"
          accent="#38bdf8"
          pins={["SDA", "SCL", "VCC", "GND"]}
        />
      )}
      {activeDests.has("relay") && (
        <PeripheralBlock
          x={220}
          y={88}
          w={88}
          h={42}
          title="Relay"
          accent="#fb923c"
          pins={["IN1", "VCC", "GND"]}
        />
      )}
      {activeDests.has("buck") && (
        <PeripheralBlock
          x={220}
          y={148}
          w={88}
          h={42}
          title="Buck 5.0V"
          accent="#f87171"
          pins={["5V OUT", "GND"]}
        />
      )}
      {activeDests.has("other") && (
        <PeripheralBlock
          x={220}
          y={118}
          w={88}
          h={42}
          title="Destino"
          accent="#94a3b8"
          pins={["SIG", "GND"]}
        />
      )}

      {/* Jumper wires + ESP32 pads */}
      {routes.map((r) => {
        const selected = selectedPinId === r.pin.id;
        const path = `M ${r.from.x} ${r.from.y} C ${r.midX} ${r.from.y}, ${r.midX} ${r.to.y}, ${r.to.x} ${r.to.y}`;
        return (
          <g key={r.pin.id}>
            {/* Wire shadow */}
            <path
              d={path}
              fill="none"
              stroke="rgba(0,0,0,0.45)"
              strokeWidth={selected ? 4.2 : 3.2}
              strokeLinecap="round"
              transform="translate(0.8 1)"
            />
            <path
              d={path}
              fill="none"
              stroke={r.color}
              strokeWidth={selected ? 3.2 : 2.2}
              strokeLinecap="round"
              opacity={selectedPinId && !selected ? 0.35 : 0.95}
              filter={selected ? "url(#wiring-glow)" : undefined}
            />
            {/* ESP32 pad */}
            <g
              role={onSelectPin ? "button" : undefined}
              tabIndex={onSelectPin ? 0 : undefined}
              style={{ cursor: onSelectPin ? "pointer" : "default" }}
              onClick={() => onSelectPin?.(r.pin.id)}
              onKeyDown={(e) => {
                if (!onSelectPin) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectPin(r.pin.id);
                }
              }}
            >
              <rect
                x={r.from.x - 10}
                y={r.from.y - 5}
                width={10}
                height={10}
                rx="1.2"
                fill={selected ? r.color : "#1e293b"}
                stroke={r.color}
                strokeWidth={selected ? 1.6 : 1}
              />
              <text
                x={r.from.x - 14}
                y={r.from.y + 2.5}
                textAnchor="end"
                fill={selected ? "#f8fafc" : "#cbd5e1"}
                fontSize="5.5"
                fontWeight={selected ? 700 : 500}
                fontFamily="ui-monospace, monospace"
              >
                {shortPinLabel(r.pin.pin)}
              </text>
            </g>
            {/* Destination terminal */}
            <circle
              cx={r.to.x}
              cy={r.to.y}
              r={selected ? 3.4 : 2.6}
              fill={selected ? r.color : "#0f172a"}
              stroke={r.color}
              strokeWidth={1.2}
            />
            {/* Signal callout near mid wire */}
            <text
              x={r.midX + 2}
              y={(r.from.y + r.to.y) / 2 - 4}
              fill={r.color}
              fontSize="5.5"
              fontWeight="700"
              fontFamily="ui-monospace, monospace"
              opacity={selectedPinId && !selected ? 0.4 : 0.95}
            >
              {r.pin.signal}
            </text>
          </g>
        );
      })}

      {/* Legend */}
      <g transform="translate(28, 188)">
        {([
          ["SDA", SIGNAL_COLORS.sda],
          ["SCL", SIGNAL_COLORS.scl],
          ["OUT", SIGNAL_COLORS.digital],
          ["GND", SIGNAL_COLORS.gnd],
          ["5V", SIGNAL_COLORS.power],
        ] as const).map(([label, color], i) => (
          <g key={label} transform={`translate(${i * 52}, 0)`}>
            <line x1="0" y1="0" x2="14" y2="0" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
            <text
              x="18"
              y="3"
              fill="#94a3b8"
              fontSize="6"
              fontFamily="ui-monospace, monospace"
            >
              {label}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function shortPinLabel(pin: string): string {
  const cleaned = pin.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 10) return cleaned;
  return cleaned.replace(/GPIO\s*/i, "G").slice(0, 10);
}

function PeripheralBlock({
  x,
  y,
  w,
  h,
  title,
  accent,
  pins,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  accent: string;
  pins: string[];
}) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx="4"
        fill="#0b1220"
        stroke={accent}
        strokeWidth="1.2"
        opacity="0.95"
      />
      <rect x={x} y={y} width={4} height={h} rx="1" fill={accent} opacity="0.85" />
      <text
        x={x + w / 2 + 2}
        y={y + 12}
        textAnchor="middle"
        fill={accent}
        fontSize="7"
        fontWeight="700"
        fontFamily="ui-monospace, monospace"
      >
        {title}
      </text>
      {pins.slice(0, 4).map((p, i) => (
        <g key={p}>
          <circle cx={x + 14} cy={y + 22 + i * 6} r="1.6" fill={accent} opacity="0.7" />
          <text
            x={x + 20}
            y={y + 24 + i * 6}
            fill="#94a3b8"
            fontSize="5"
            fontFamily="ui-monospace, monospace"
          >
            {p}
          </text>
        </g>
      ))}
    </g>
  );
}
